import type { CountryAiMode, CountryGenResult } from './multiCountryBulkEdit.types';

import { countryGenResultHasTexts } from './translateBulkEditCountryTexts';

/** Per country: one boolean row per text batch (flat → single row). */
export type TextPickBatches = boolean[][];

export function defaultTextPicksForResult(r: CountryGenResult): TextPickBatches {
  const tb = r.textBatches;
  if (tb?.length) return tb.map((row) => row.map(() => true));
  if (r.texts.length) return [r.texts.map(() => true)];
  return [];
}

export function picksMatchResult(picks: TextPickBatches | undefined, r: CountryGenResult): boolean {
  if (!picks?.length) return false;
  const tb = r.textBatches;
  if (tb?.length) {
    if (picks.length !== tb.length) return false;
    return tb.every((row, i) => (picks[i]?.length ?? 0) === row.length);
  }
  if (r.texts.length) return picks.length === 1 && picks[0]!.length === r.texts.length;
  return picks.length === 0;
}

export function filterTextsByPicks(texts: string[], flags: boolean[] | undefined): string[] {
  const f = flags?.length === texts.length ? flags : texts.map(() => true);
  return texts.filter((_, i) => f[i]);
}

export function buildFilteredMergeBatches(
  globalCountryPerDrop: string[],
  resultsByCountry: Record<string, CountryGenResult>,
  picks: Record<string, TextPickBatches | undefined>,
): string[][] {
  const localDrop = new Map<string, number>();
  return globalCountryPerDrop.map((ck) => {
    const li = localDrop.get(ck) ?? 0;
    localDrop.set(ck, li + 1);
    const r = resultsByCountry[ck]!;
    const batch = r.textBatches?.[li] ?? (li === 0 ? r.texts : []);
    const row = picks[ck]?.[li] ?? picks[ck]?.[0];
    const flags = row && row.length === batch.length ? row : batch.map(() => true);
    return batch.filter((_, i) => flags[i]);
  });
}

export function patchBatchAfterRegenerate(
  r: CountryGenResult,
  batchIdx: number,
  replaceIndices: number[],
  newTexts: string[],
  newTranslations?: string[],
): CountryGenResult {
  const sorted = [...replaceIndices].sort((a, b) => a - b);
  if (r.textBatches?.length) {
    const tb = r.textBatches.map((b) => [...b]);
    const batch = [...(tb[batchIdx] ?? [])];
    let ri = 0;
    for (const i of sorted) {
      if (ri < newTexts.length) batch[i] = newTexts[ri]!;
      ri += 1;
    }
    tb[batchIdx] = batch;
    const trb = r.translationBatches?.map((row) => [...row]);
    if (trb && batchIdx < trb.length) {
      const trow = [...trb[batchIdx]!];
      let ti = 0;
      for (const i of sorted) {
        if (newTranslations && ti < newTranslations.length) trow[i] = newTranslations[ti]!;
        else trow[i] = '';
        ti += 1;
      }
      trb[batchIdx] = trow;
    }
    return { ...r, textBatches: tb, translationBatches: trb, texts: tb.flat() };
  }
  const texts = [...r.texts];
  let ri = 0;
  for (const i of sorted) {
    if (ri < newTexts.length) texts[i] = newTexts[ri]!;
    ri += 1;
  }
  const translations = r.translations?.map((t) => t);
  if (translations?.length) {
    let ti = 0;
    for (const i of sorted) {
      if (newTranslations && ti < newTranslations.length) translations[i] = newTranslations[ti]!;
      else translations[i] = '';
      ti += 1;
    }
  }
  return { ...r, texts, translations };
}

export function reconcileTextPicks(
  prev: Record<string, TextPickBatches>,
  countryKeys: string[],
  resultsByCountry: Record<string, CountryGenResult>,
): Record<string, TextPickBatches> {
  const next = { ...prev };
  for (const ck of countryKeys) {
    const r = resultsByCountry[ck];
    if (!r || !countryGenResultHasTexts(r)) {
      delete next[ck];
      continue;
    }
    if (!picksMatchResult(next[ck], r)) next[ck] = defaultTextPicksForResult(r);
  }
  for (const k of Object.keys(next)) {
    if (!countryKeys.includes(k) || !resultsByCountry[k]) delete next[k];
  }
  return next;
}

export function pushBulkEditTextPicksToForm(p: {
  hasAi: boolean;
  textMode: CountryAiMode;
  countryKeys: string[];
  globalCountryPerDrop: string[];
  results: Record<string, CountryGenResult>;
  picks: Record<string, TextPickBatches | undefined>;
  syncPerDropSmsTexts?: (batches: string[][]) => void;
  syncSmsTextsByCountry?: (by: Record<string, string[]>) => void;
}): void {
  if (!p.hasAi) return;
  if (p.textMode === 'per_drop') {
    p.syncPerDropSmsTexts?.(buildFilteredMergeBatches(p.globalCountryPerDrop, p.results, p.picks));
    return;
  }
  const by: Record<string, string[]> = {};
  for (const ck of p.countryKeys) {
    const r = p.results[ck];
    if (r) by[ck] = filterTextsByPicks(r.texts, p.picks[ck]?.[0]);
  }
  p.syncSmsTextsByCountry?.(by);
}
